import React, { useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tab,
    Tabs,
    Box,
    Grid,
    MenuItem,
    TextField,
    InputAdornment,
    IconButton,
} from '@mui/material'
import commonStyles from 'styles/CommonStyles'
import { useFormik } from 'formik'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import * as yup from 'yup'
import UploadUserCSV from 'components/User/UploadUserCSV'

const validationSchema = yup.object({
    firstname: yup
        .string('Enter your first name')
        .min(2, 'First name should be of minimum 2 characters length')
        .required('First name is required'),
    lastname: yup
        .string('Enter your last name')
        .min(2, 'Last name should be of minimum 2 characters length')
        .required('Last name is required'),
    email: yup
        .string('Enter your email')
        .email('Enter a valid email')
        .required('Email is required'),
    affiliation: yup
        .string('Enter your affiliation')
        .required('Affiliation is required'),
    title: yup.string('Enter your title').required('Title is required'),
    password: yup
        .string('Enter your password')
        .min(8, 'Password should be of minimum 8 characters length')
        .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
            'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
        )
        .required('Password is required'),
    role: yup.string('Select user role').required('Role is required'),
})

export default ({ open, handleClose, handleFormSubmit }) => {
    const [activeTab, setActiveTab] = useState(0)
    const [showPassword, setShowPassword] = useState(false)
    const commonClasses = commonStyles()

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue)
    }

    const formik = useFormik({
        initialValues: {
            email: '',
            firstname: '',
            lastname: '',
            password: '',
            affiliation: '',
            title: '',
            role: '',
        },
        validationSchema: validationSchema,
        onSubmit: (values) => submitForm(values),
    })
    const submitForm = (values) => {
        let newUser = {
            email: values.email,
            firstname: values.firstname,
            lastname: values.lastname,
            password: values.password || null,
            affiliation: values.affiliation,
            title: values.title,
            role: values.role,
        }
        handleFormSubmit(newUser)
        formik.resetForm()
    }
    const roles = {
        ADMIN: 'Admin',
        USER: 'User',
    }

    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open={open}
            onClose={(_, reason) => {
                if (reason !== 'backdropClick') {
                    handleClose()
                }
            }}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">Add New Users</DialogTitle>
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
            >
                <Tab label="Create User" />
                <Tab label="Upload CSV" />
            </Tabs>
            <DialogContent dividers className={commonClasses.dialogForm}>
                {activeTab === 0 && (
                    <Box component="form" onSubmit={formik.handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    autoComplete="fname"
                                    name="firstname"
                                    variant="outlined"
                                    fullWidth
                                    id="firstname"
                                    label="First Name"
                                    autoFocus
                                    value={formik.values.firstname}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.firstname &&
                                        Boolean(formik.errors.firstname)
                                    }
                                    helperText={
                                        formik.touched.firstname &&
                                        formik.errors.firstname
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    id="lastname"
                                    label="Last Name"
                                    name="lastname"
                                    autoComplete="lname"
                                    value={formik.values.lastname}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.lastname &&
                                        Boolean(formik.errors.lastname)
                                    }
                                    helperText={
                                        formik.touched.lastname &&
                                        formik.errors.lastname
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    id="role"
                                    select
                                    label="Role"
                                    name="role"
                                    value={formik.values.role}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.role &&
                                        Boolean(formik.errors.role)
                                    }
                                    helperText={
                                        formik.touched.role &&
                                        formik.errors.role
                                    }
                                >
                                    {Object.keys(roles).map((role) => (
                                        <MenuItem key={role} value={role}>
                                            {roles[role]}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    id="email"
                                    label="Email Address"
                                    name="email"
                                    autoComplete="email"
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.email &&
                                        Boolean(formik.errors.email)
                                    }
                                    helperText={
                                        formik.touched.email &&
                                        formik.errors.email
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    id="affiliation"
                                    label="Affiliation"
                                    name="affiliation"
                                    autoComplete="affiliation"
                                    value={formik.values.affiliation}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.affiliation &&
                                        Boolean(formik.errors.affiliation)
                                    }
                                    helperText={
                                        formik.touched.affiliation &&
                                        formik.errors.affiliation
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    id="title"
                                    label="Title"
                                    name="title"
                                    autoComplete="title"
                                    value={formik.values.title}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.title &&
                                        Boolean(formik.errors.title)
                                    }
                                    helperText={
                                        formik.touched.title &&
                                        formik.errors.title
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    name="password"
                                    label="Password"
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    autoComplete="current-password"
                                    value={formik.values.password}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.password &&
                                        Boolean(formik.errors.password)
                                    }
                                    helperText={
                                        formik.touched.password &&
                                        formik.errors.password
                                    }
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() =>
                                                        setShowPassword(
                                                            (prev) => !prev,
                                                        )
                                                    }
                                                    edge="end"
                                                    aria-label={
                                                        showPassword
                                                            ? 'Hide password'
                                                            : 'Show password'
                                                    }
                                                >
                                                    {showPassword ? (
                                                        <VisibilityOff />
                                                    ) : (
                                                        <Visibility />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                )}
                {activeTab === 1 && (
                    <UploadUserCSV onUploadComplete={() => {}} />
                )}
            </DialogContent>
            <DialogActions>
                {activeTab === 0 && (
                    <>
                        <Button onClick={handleClose} color="secondary">
                            Cancel
                        </Button>
                        <Button
                            onClick={formik.handleSubmit}
                            color="primary"
                            variant="contained"
                        >
                            Save
                        </Button>
                    </>
                )}
                {activeTab === 1 && (
                    <Button onClick={handleClose} color="secondary">
                        Close
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}
